import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Stethoscope, FileText, ChevronLeft, Shield, Users, Briefcase, BookOpen, Play, ExternalLink, Palette, Sparkles, X, CheckCircle2, RefreshCw, Share2, Copy, Clock, Code, Heart, BarChart3, Info, TrendingUp, Zap, Activity, MessageSquare, ClipboardList, Smartphone, Monitor, Pill, Thermometer, Inbox, Send, CalendarDays, Accessibility, ArrowRightCircle, Medal, Gavel, UserCheck, ListChecks, MoreHorizontal, Building2, BadgeCheck, Star, LifeBuoy } from 'lucide-react';
import { BrandEmblem, BrandMotto } from '../BrandEmblem';
import { TutorialSlides } from './TutorialSlides';
import TutorialSlideshow from '../TutorialSlideshow';
import { MOOD_THEMES } from '../../constants/mediscribe';
import { APP_VERSION } from '../VersionChecker';
import ProviderDashboard from '../ProviderDashboard';
import { getCurrentReleaseItems, getCurrentRelease, getDraftItems, saveDraftItems, clearDraftItems, TAG_COLORS } from '../../data/releaseNotes';
import { toast } from 'sonner';

const API = `${window.__WR_API_BASE__}/api`;


const HOTSPOT_DATA = {
  time: { title: 'Time', desc: 'The metric that defines provider ROI and predicts burnout within 5 years of licensure.' },
  sand: { title: 'The Sand', desc: 'Running out of time, energy, self-care, life balance, quality care.' },
  settles: { title: 'What Settles', desc: 'Quality care — pushed to the bottom of priorities by volume metrics and impossible timelines.' },
  stethoscope: { title: 'The Stethoscope', desc: 'The provider\'s lifeline — listening, diagnosing, advocating. The instrument that connects healer to patient.' },
  convergence: { title: 'The Convergence Point', desc: 'Where it all meets. WriteRight PCM+ reconciles quality and quantity — what the system said was impossible. One input. Everything generated. Simultaneously.', brand: true },
};

/** Positions for each hotspot dot on the emblem + tooltip side
 *  Ordered clockwise for narrative flow: time (top) → sand (upper bulb) →
 *  settles (bottom bulb) → stethoscope (left curve) → convergence (CENTER, the climax).
 *  Percentages are tuned so the dots sit ON the visual elements, not floating in the void
 *  around the image. `side` controls where the floating tooltip appears. */
const HOTSPOT_POS = {
  time:         { top: '12%', left: '50%', side: 'below' },
  sand:         { top: '36%', left: '50%', side: 'right' },
  settles:      { top: '72%', left: '50%', side: 'above' },
  stethoscope:  { top: '50%', left: '38%', side: 'left' },
  convergence:  { top: '50%', left: '50%', side: 'right' }, // CENTER — the crossover climax
};

/** Desktop tooltip placement styles keyed by side */
const TOOLTIP_SIDE_STYLES = {
  below: { top: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' },
  above: { bottom: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' },
  right: { left: 'calc(100% + 14px)', top: '50%', transform: 'translateY(-50%)' },
  left:  { right: 'calc(100% + 14px)', top: '50%', transform: 'translateY(-50%)', textAlign: 'right' },
};

/**
 * Role Selector - Initial screen for choosing Provider vs Patient
 * Now includes mood/theme persistence - landing page uses last selected mood
 */
export const RoleSelector = ({ onSelectRole, onShowAuth, currentMood, currentMoodIndex, setCurrentMoodIndex, user, onLegalNavigate }) => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [showInteractiveTutorial, setShowInteractiveTutorial] = useState(null); // 'patient' or 'provider'
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [whatsNewSlide, setWhatsNewSlide] = useState(0);

  // Auto-open the What's New modal once per version for logged-in users.
  // Tracked in localStorage so each version only auto-opens once per device.
  useEffect(() => {
    if (!user || user.is_guest) return;
    try {
      const raw = localStorage.getItem('writeright_whats_new_seen_versions');
      const seen = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(seen) || !seen.includes(APP_VERSION)) {
        // Small delay so the role selector renders first, then the modal pops
        const t = setTimeout(() => setShowWhatsNew(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* ignore malformed storage */ }
  }, [user]);

  // Mark the current version as seen whenever the modal closes
  const markWhatsNewSeen = () => {
    try {
      const raw = localStorage.getItem('writeright_whats_new_seen_versions');
      const seen = raw ? JSON.parse(raw) : [];
      if (Array.isArray(seen) && !seen.includes(APP_VERSION)) {
        localStorage.setItem(
          'writeright_whats_new_seen_versions',
          JSON.stringify([...seen, APP_VERSION].slice(-20))
        );
      } else if (!Array.isArray(seen)) {
        localStorage.setItem('writeright_whats_new_seen_versions', JSON.stringify([APP_VERSION]));
      }
    } catch { /* ignore */ }
  };

  const closeWhatsNew = () => {
    markWhatsNewSeen();
    setShowWhatsNew(false);
  };
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showOriginStory, setShowOriginStory] = useState(false);
  const [showEmblem, setShowEmblem] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);

  // Escape key closes the hourglass story overlay — multiple ways out
  useEffect(() => {
    if (!showEmblem) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (activeHotspot) {
          setActiveHotspot(null);
        } else {
          setShowEmblem(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEmblem, activeHotspot]);
  const [copiedShare, setCopiedShare] = useState(false);
  const [showProviderStats, setShowProviderStats] = useState(false);
  const [activeStatPopup, setActiveStatPopup] = useState(null); // 'days', 'commits', 'lines'
  
  // Momentum tracker state
  const [momentumStats, setMomentumStats] = useState(null);
  const [isLoadingMomentum, setIsLoadingMomentum] = useState(true);
  
  // Fetch momentum stats on mount
  useEffect(() => {
    const fetchMomentumStats = async () => {
      try {
        const response = await axios.get(`${API}/public/momentum-stats`);
        setMomentumStats(response.data);
      } catch (error) {
        console.error('Failed to fetch momentum stats:', error);
      } finally {
        setIsLoadingMomentum(false);
      }
    };
    
    fetchMomentumStats();
  }, []);
  
  // Stat info content
  const statInfo = {
    days: {
      title: '30 Days',
      text: 'Zero prior coding experience. From concept to functional app in 30 days through AI-assisted development and pure determination.',
      icon: '⚡'
    },
    commits: {
      title: '1,064 Commits',
      text: 'Each commit = a save point. 1,064 iterations of building, fixing, and improving. The product of relentless refinement.',
      icon: '🔄'
    },
    lines: {
      title: '38.6K Lines',
      text: 'Equivalent to a small commercial app—React frontend + Python backend + MongoDB. Built by a clinician, not a developer.',
      icon: '💻'
    }
  };
  
  // Toggle stat popup (tap to toggle on mobile, also triggered by hover on desktop)
  const toggleStatPopup = (stat) => {
    setActiveStatPopup(prev => prev === stat ? null : stat);
  };
  const showStatPopup = (stat) => {
    setActiveStatPopup(stat);
  };
  const hideStatPopup = () => {
    setActiveStatPopup(null);
  };
  
  // Share message for colleagues
  const shareMessage = `Hey \u2014 found a tool that actually saves charting time. You talk through your encounter once, it simultaneously generates your note + referrals + imaging orders + profile letter. ICD-10 codes auto-detect.

Built by a military NP in 30 days. 38K lines of code. Free, no login required.

Put it through its paces for me? I\u2019m running full error tracking so I\u2019ll probably catch issues before you even report them \u2014 but if you find something, let me know. Enhancements welcome too.

\ud83d\udd17 https://pcm-write-time-1.emergent.host/
\ud83d\udd11 Password: WriteRight2025`;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareMessage;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 3000);
    }
  };
  
  // Hard refresh - clear caches and reload
  const handleHardRefresh = async () => {
    setIsRefreshing(true);
    
    // Clear all caches
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }
    
    // Clear version from localStorage
    localStorage.removeItem('writeright_app_version');
    localStorage.removeItem('writeright_dismissed_version');
    
    // Hard refresh
    window.location.reload(true);
  };
  
  // Cycle to next mood
  const cycleMood = () => {
    if (setCurrentMoodIndex) {
      setCurrentMoodIndex((prev) => (prev + 1) % MOOD_THEMES.length);
    }
  };
  
  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center p-3 sm:p-4 pb-36 pt-14 sm:pt-4 overflow-y-auto" 
      style={{ 
        minHeight: '100dvh',
        '--background': '220 45% 8%',
        '--foreground': '215 25% 88%',
        '--card': '220 40% 12%',
        '--card-foreground': '215 25% 92%',
        '--border': '220 30% 20%',
        '--primary': '30 80% 52%',
        '--primary-foreground': '220 45% 6%',
        '--secondary': '220 35% 15%',
        '--secondary-foreground': '215 25% 85%',
        '--muted': '220 30% 12%',
        '--muted-foreground': '215 15% 55%',
        backgroundColor: 'hsl(220, 45%, 8%)',
        color: 'hsl(215, 25%, 88%)',
      }}>
      {/* Theme/Mood Picker - Fixed position top left - smaller on mobile */}
      <div className="fixed top-2 left-2 sm:top-4 sm:left-4 z-50 flex items-center gap-1 sm:gap-2">
        <button
          onClick={cycleMood}
          className="p-1.5 sm:p-2 rounded-lg transition-all hover:scale-105 shadow-lg bg-card/80 hover:bg-card border border-border"
          title={`Current: ${currentMood?.name || 'Theme'} - Click to change`}
          data-testid="landing-mood-btn"
        >
          <span className="text-base sm:text-xl">{currentMood?.emoji || '🎨'}</span>
        </button>
        <button
          onClick={() => setShowThemePicker(!showThemePicker)}
          className="p-1.5 sm:p-2 rounded-lg transition-all hover:scale-105 bg-card/50 hover:bg-card border border-border/50"
          title="Show all themes"
        >
          <Palette className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
        </button>
      </div>
      
      {/* Theme Picker Dropdown - adjusted position for mobile */}
      {showThemePicker && (
        <div className="fixed top-12 sm:top-16 left-2 sm:left-4 z-50 p-2 sm:p-3 rounded-xl bg-card border border-border shadow-xl max-h-[60vh] overflow-y-auto">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Choose Theme:</p>
          <div className="grid grid-cols-5 gap-1.5">
            {MOOD_THEMES.map((mood, idx) => (
              <button
                key={mood.id}
                onClick={() => {
                  setCurrentMoodIndex(idx);
                  setShowThemePicker(false);
                }}
                className={`p-2 rounded-lg transition-all hover:scale-110 ${
                  currentMoodIndex === idx ? 'ring-2 ring-primary bg-primary/20' : 'bg-secondary/30 hover:bg-secondary'
                }`}
                title={mood.name}
              >
                <span className="text-lg">{mood.emoji}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Sign In Button - Fixed position top right */}
      {onShowAuth && (
        <div className="fixed top-3 right-3 sm:top-4 sm:right-4 z-50 flex items-center gap-1.5 sm:gap-2">
          {/* Refresh Button — Feb 28, 2026: bumped alpha + brighter icon
              + dark backdrop so it stays legible on every mood theme.    */}
          <button
            onClick={handleHardRefresh}
            disabled={isRefreshing}
            className="p-1.5 sm:p-2 text-xs font-medium transition-all hover:scale-105 disabled:opacity-50"
            data-testid="refresh-btn"
            title="Refresh app & clear cache"
            style={{
              borderRadius: '4px',
              background: 'linear-gradient(180deg, rgba(59,130,246,0.45) 0%, rgba(15,23,42,0.85) 100%)',
              color: '#dbeafe',
              border: '1px solid rgba(96,165,250,0.85)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* What's New / Version Button — Feb 28, 2026: brighter gold
              foreground (#fbbf24 vs #daa520) and a darker solid bronze base
              so the text stays AA-legible on Midnight/Charcoal moods.      */}
          <button
            onClick={() => setShowWhatsNew(true)}
            className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold transition-all hover:scale-[1.02] active:scale-95 flex items-center gap-1.5 sm:gap-2"
            style={{
              background: 'linear-gradient(180deg, #1a0e00 0%, #0d0700 100%)',
              color: '#fbbf24',
              boxShadow: 'inset 0 1px 0 rgba(251,191,36,0.35), 0 2px 10px rgba(0,0,0,0.55)',
              border: '1.5px solid #b45309',
              borderRadius: '4px',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              textShadow: '0 1px 2px rgba(0,0,0,0.65)',
            }}
            data-testid="whats-new-btn"
          >
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: '#fcd34d' }} />
            <span>What's New {APP_VERSION}</span>
          </button>

          {/* Signed in pill — Feb 28, 2026: brighter emerald foreground +
              opaque dark gradient so the checkmark + label pop on any mood. */}
          {user && !user.is_guest ? (
            <div
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold flex items-center gap-1.5"
              style={{
                borderRadius: '4px',
                background: 'linear-gradient(180deg, rgba(16,185,129,0.55) 0%, rgba(6,78,59,0.85) 100%)',
                color: '#a7f3d0',
                border: '1px solid rgba(52,211,153,0.85)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                textShadow: '0 1px 2px rgba(0,0,0,0.45)',
              }}
              data-testid="signed-in-pill"
              title={`Signed in as ${user.name || user.username || user.email}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Signed in</span>
              <span className="sm:hidden">✓</span>
            </div>
          ) : (
            <button
              onClick={onShowAuth}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-all hover:scale-105"
              style={{
                borderRadius: '4px',
                background: 'linear-gradient(180deg, #DAA520 0%, #8B6914 100%)',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                border: '1px solid #B8860B',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              }}
              data-testid="sign-in-btn"
            >
              Sign In
            </button>
          )}
        </div>
      )}
      
      <div className="max-w-md sm:max-w-lg md:max-w-2xl lg:max-w-3xl w-full space-y-3 sm:space-y-5 relative">
        {/* Quick Stats Row - Compact to fit one row on mobile - Now tappable */}
        <div className="flex justify-center gap-1.5 sm:gap-3 pt-20 sm:pt-0">
          <button 
            onClick={() => toggleStatPopup('days')}
            onMouseEnter={() => showStatPopup('days')}
            onMouseLeave={hideStatPopup}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 transition-all hover:scale-110 active:scale-95"
            style={{
              background: activeStatPopup === 'days' 
                ? 'linear-gradient(180deg, #2a1a00 0%, #1a0a00 100%)'
                : 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
              border: activeStatPopup === 'days' ? '2px solid #daa520' : '1px solid #8b4513',
              borderRadius: '4px',
              boxShadow: activeStatPopup === 'days' 
                ? '0 0 12px rgba(218, 165, 32, 0.4), 0 2px 8px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 2px rgba(0,0,0,0.5)',
            }}
            data-testid="landing-stat-days"
          >
            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" />
            <span 
              className="text-[10px] sm:text-sm font-semibold whitespace-nowrap"
              style={{ 
                background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >30 Days</span>
          </button>
          <button 
            onClick={() => toggleStatPopup('lines')}
            onMouseEnter={() => showStatPopup('lines')}
            onMouseLeave={hideStatPopup}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 transition-all hover:scale-110 active:scale-95"
            style={{
              background: activeStatPopup === 'lines' 
                ? 'linear-gradient(180deg, #2a1a00 0%, #1a0a00 100%)'
                : 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
              border: activeStatPopup === 'lines' ? '2px solid #daa520' : '1px solid #8b4513',
              borderRadius: '4px',
              boxShadow: activeStatPopup === 'lines' 
                ? '0 0 12px rgba(218, 165, 32, 0.4), 0 2px 8px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 2px rgba(0,0,0,0.5)',
            }}
            data-testid="landing-stat-lines"
          >
            <Code className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" />
            <span 
              className="text-[10px] sm:text-sm font-semibold whitespace-nowrap"
              style={{ 
                background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >38.6K Lines</span>
          </button>
          <button 
            onClick={() => toggleStatPopup('commits')}
            onMouseEnter={() => showStatPopup('commits')}
            onMouseLeave={hideStatPopup}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 transition-all hover:scale-110 active:scale-95"
            style={{
              background: activeStatPopup === 'commits' 
                ? 'linear-gradient(180deg, #2a1a00 0%, #1a0a00 100%)'
                : 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
              border: activeStatPopup === 'commits' ? '2px solid #daa520' : '1px solid #8b4513',
              borderRadius: '4px',
              boxShadow: activeStatPopup === 'commits' 
                ? '0 0 12px rgba(218, 165, 32, 0.4), 0 2px 8px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 2px rgba(0,0,0,0.5)',
            }}
            data-testid="landing-stat-commits"
          >
            <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" />
            <span 
              className="text-[10px] sm:text-sm font-semibold whitespace-nowrap"
              style={{ 
                background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >1,064 Commits</span>
          </button>
        </div>
        
        {/* Stat Info Popup - Shows below stats when tapped on landing page */}
        {activeStatPopup && (
          <div 
            className="mx-auto max-w-sm p-3 animate-in fade-in slide-in-from-top-2 duration-200"
            style={{
              background: 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
              border: '1px solid #8b4513',
              borderRadius: '4px',
            }}
          >
            <div className="flex items-start gap-3">
              <span className="text-xl">{statInfo[activeStatPopup].icon}</span>
              <div className="flex-1">
                <h4 
                  className="font-bold text-sm mb-1"
                  style={{ 
                    background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >{statInfo[activeStatPopup].title}</h4>
                <p className="text-xs text-amber-100/80 leading-relaxed">
                  {statInfo[activeStatPopup].text}
                </p>
              </div>
              <button 
                onClick={() => setActiveStatPopup(null)}
                className="p-1 hover:bg-amber-500/20 rounded text-amber-100/50 hover:text-amber-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Brand Emblem */}
        <div className="flex flex-col items-center justify-center relative z-10 shrink-0" style={{ '--brand-text-color': 'var(--foreground)' }}>
          <div className="hidden sm:block">
            <BrandEmblem size="lg" />
          </div>
          <div className="block sm:hidden">
            <BrandEmblem size="md" />
          </div>
        </div>
        
        {/* Tagline */}
        <div className="text-center px-3 space-y-1.5">
          <p
            className="text-sm sm:text-base font-semibold leading-relaxed"
            style={{
              background: 'linear-gradient(180deg, #d4af37 0%, #b8860b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Converging quality and quantity.
            <br />
            <span className="font-black" style={{
              background: 'linear-gradient(135deg, #fff 0%, #ffd700 50%, #daa520 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>Simultaneously.</span>{' '}Without&nbsp;compromise.
          </p>
          <p className="text-[11px] sm:text-xs text-amber-100/40 italic">
            Because the system won&rsquo;t change &mdash; but your workflow&nbsp;can.
          </p>
        </div>
        
        {/* About WriteRight Button — ABOVE the role selection tiles */}
        <div className="flex justify-center pt-3 sm:pt-4">
          <div
            style={{
              padding: '2px',
              background: 'linear-gradient(135deg, #cd853f 0%, #8b4513 50%, #daa520 100%)',
              borderRadius: '4px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
          >
            <button
              onClick={() => setShowTutorial(true)}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-3.5 text-sm sm:text-base md:text-lg font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                color: '#1a0a00',
                borderRadius: '2px',
              }}
              data-testid="about-writeright-btn"
            >
              <Play className="w-4 h-4 sm:w-5 sm:h-5" />
              About WriteRight PCM+
            </button>
          </div>
        </div>
        
        
        {/* Role Selection - Main CTA */}
        <div className="space-y-3 sm:space-y-4 pt-2 sm:pt-4">
          <p 
            className="text-center text-sm sm:text-base font-semibold"
            style={{ 
              background: 'linear-gradient(180deg, #d4af37 0%, #b8860b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >How can we help you today?</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 items-stretch">
            {/* Provider Option - Premium Bronze Card */}
            <div className="flex flex-col gap-1.5 sm:gap-2">
              <div
                className="flex-1 flex flex-col"
                style={{
                  padding: '2px',
                  background: 'linear-gradient(135deg, #cd853f 0%, #8b4513 30%, #daa520 70%, #8b4513 100%)',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                }}
              >
                <button
                  onClick={() => onSelectRole('provider')}
                  className="w-full flex-1 group p-3 sm:p-5 md:p-6 transition-all duration-300 overflow-hidden flex flex-col items-center justify-center"
                  style={{
                    background: 'linear-gradient(180deg, #1a0a00 0%, #0d0500 50%, #1a0a00 100%)',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6)',
                    borderRadius: '2px',
                  }}
                  data-testid="role-provider"
                >
                  <div className="flex flex-col items-center justify-center text-center gap-2 sm:gap-3">
                    <div 
                      className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all group-hover:scale-105 mx-auto"
                      style={{
                        background: 'linear-gradient(135deg, #8b4513 0%, #daa520 50%, #8b4513 100%)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                      }}
                    >
                      <Stethoscope className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-amber-100" />
                    </div>
                    <div className="text-center">
                      <h3 
                        className="text-lg sm:text-xl md:text-2xl font-bold"
                        style={{ 
                          background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >I'm a Provider</h3>
                      <p className="text-xs sm:text-sm text-amber-100/70">NP, PA, MD, DO, Medic</p>
                    </div>
                  </div>
                  <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-amber-100/60 whitespace-normal text-center" style={{ wordBreak: 'break-word' }}>
                    One encounter, one generation — notes, referrals, imaging orders, admin letters & ICD-10 codes all&nbsp;at&nbsp;once
                  </p>
                </button>
              </div>
              <button
                onClick={() => setShowInteractiveTutorial('provider')}
                className="w-full inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg relative z-20"
                style={{
                  background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                  color: '#1a0a00',
                  borderRadius: '4px',
                }}
                data-testid="provider-tutorial-btn"
              >
                <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Provider Tutorial
              </button>
            </div>
            
            {/* Patient Option - Premium Bronze Card (matching border) */}
            <div className="flex flex-col gap-1.5 sm:gap-2">
              <div
                className="flex-1 flex flex-col"
                style={{
                  padding: '2px',
                  background: 'linear-gradient(135deg, #cd853f 0%, #8b4513 30%, #daa520 70%, #8b4513 100%)',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                }}
              >
                <button
                  onClick={() => onSelectRole('patient')}
                  className="w-full flex-1 group p-3 sm:p-5 md:p-6 transition-all duration-300 overflow-hidden flex flex-col items-center justify-center"
                  style={{
                    background: 'linear-gradient(180deg, #0a1a0d 0%, #050d07 50%, #0a1a0d 100%)',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6)',
                    borderRadius: '2px',
                  }}
                  data-testid="role-patient"
                >
                  <div className="flex flex-col items-center justify-center text-center gap-2 sm:gap-3">
                    <div 
                      className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all group-hover:scale-105 mx-auto"
                      style={{
                        background: 'linear-gradient(135deg, #1a3d2a 0%, #3d7a4d 50%, #1a3d2a 100%)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                      }}
                    >
                      <FileText className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-emerald-100" />
                    </div>
                    <div className="text-center">
                      <h3 
                        className="text-lg sm:text-xl md:text-2xl font-bold"
                        style={{ 
                          background: 'linear-gradient(180deg, #4ade80 0%, #22c55e 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >I Need a Letter</h3>
                      <p className="text-xs sm:text-sm text-emerald-100/70">Patient, Soldier, or Beneficiary</p>
                    </div>
                  </div>
                  <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-emerald-100/60 whitespace-normal text-center" style={{ wordBreak: 'break-word' }}>
                    Pre-fill admin letters to bring to your provider: FMLA, ADA, duty limitations, PCS stabilization
                  </p>
                </button>
              </div>
              <button
                onClick={() => setShowInteractiveTutorial('patient')}
                className="w-full inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg relative z-20"
                style={{
                  background: 'linear-gradient(180deg, #4ade80 0%, #22c55e 50%, #16a34a 100%)',
                  color: '#0a1a0d',
                  borderRadius: '4px',
                }}
                data-testid="patient-tutorial-btn"
              >
                <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Patient Tutorial
              </button>
            </div>
          </div>
        </div>
        
        {/* Secondary Actions Row - Premium styled */}
        <div className="flex justify-center gap-3 flex-wrap pt-2">
          {/* My Stats Button - Only for logged in users */}
          {user && (
            <div
              style={{
                padding: '1px',
                background: 'linear-gradient(135deg, #2d5a3d 0%, #3d7a4d 100%)',
                borderRadius: '4px',
              }}
            >
              <button
                onClick={() => setShowProviderStats(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(180deg, #0a1a0d 0%, #050d07 100%)',
                  color: '#4ade80',
                  borderRadius: '3px',
                }}
                data-testid="my-stats-btn"
              >
                <BarChart3 className="w-4 h-4" />
                My Stats
              </button>
            </div>
          )}
          
          {/* Share Button */}
          <div
            style={{
              padding: '1px',
              background: copiedShare 
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                : 'linear-gradient(135deg, #8b4513 0%, #daa520 100%)',
              borderRadius: '4px',
            }}
          >
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-all hover:scale-[1.02]"
              style={{
                background: copiedShare 
                  ? 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)'
                  : 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
                color: copiedShare ? '#fff' : '#ffd700',
                borderRadius: '3px',
              }}
              data-testid="share-colleagues-btn"
            >
              {copiedShare ? <CheckCircle2 className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copiedShare ? 'Copied!' : 'Share'}
            </button>
          </div>
        </div>
        
        {/* Footer - Premium styled */}
        <p className="text-center text-[10px] sm:text-xs text-amber-100/50 px-2">
          Zero PII stored &bull; Works in Guest Mode &bull; HIPAA-conscious&nbsp;design
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-1">
          <button onClick={() => onLegalNavigate && onLegalNavigate('terms')} className="text-[10px] text-amber-100/40 hover:text-amber-100/70 underline-offset-2 hover:underline transition-colors" data-testid="landing-terms-link">Terms</button>
          <button onClick={() => onLegalNavigate && onLegalNavigate('privacy')} className="text-[10px] text-amber-100/40 hover:text-amber-100/70 underline-offset-2 hover:underline transition-colors" data-testid="landing-privacy-link">Privacy</button>
          <button onClick={() => onLegalNavigate && onLegalNavigate('hipaa')} className="text-[10px] text-amber-100/40 hover:text-amber-100/70 underline-offset-2 hover:underline transition-colors" data-testid="landing-hipaa-link">HIPAA</button>
        </div>

        {/* Origin Story Badge — TEXT FIRST, then emblem */}
        <div className="flex justify-center pt-3 sm:pt-4">
          <button
            onClick={() => setShowOriginStory(true)}
            className="group w-full max-w-md sm:max-w-lg"
            style={{
              padding: '2px',
              background: 'linear-gradient(135deg, #cd853f 0%, #8b4513 20%, #daa520 50%, #8b4513 80%, #cd853f 100%)',
              borderRadius: '4px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
            data-testid="origin-story-btn"
          >
            <div 
              className="px-5 sm:px-8 md:px-10 py-4 sm:py-5 flex flex-col items-center gap-2 sm:gap-3 transition-transform group-hover:scale-[1.01] group-active:scale-[0.98]"
              style={{
                background: 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
                borderRadius: '2px',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)',
              }}
            >
              <p 
                className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-center"
                style={{ 
                  background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Built From Burnout. Built to&nbsp;Last.
              </p>
              <img 
                src="/emblem_icon.png" 
                alt="WriteRight PCM+" 
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain opacity-90 mx-auto block"
                style={{ maxHeight: '80px' }}
              />
              <p 
                className="text-sm sm:text-base md:text-lg font-semibold text-center"
                style={{ 
                  background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                30 Days. 25+ Features. One&nbsp;Mission.
              </p>
              <p className="text-xs sm:text-sm text-amber-100/70 text-center" style={{ textWrap: 'balance' }}>
                One click generates notes, referrals, orders & letters —&nbsp;SIMULTANEOUSLY.
              </p>
            </div>
          </button>
        </div>

        {/* Hourglass & Stethoscope — Subtle link */}
        <div className="flex justify-center pt-3" data-testid="hourglass-emblem-section">
          <button
            onClick={() => setShowEmblem(true)}
            className="group cursor-pointer bg-transparent border-none outline-none flex flex-col items-center gap-1"
            data-testid="emblem-open-btn"
          >
            <span
              className="text-base sm:text-lg md:text-xl font-bold tracking-[0.15em] uppercase whitespace-nowrap"
              style={{
                background: 'linear-gradient(90deg, #b8860b 0%, #daa520 25%, #ffd700 50%, #daa520 75%, #b8860b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 8px rgba(218,165,32,0.4))',
              }}
            >
              Why an Hourglass & Stethoscope?
            </span>
            <Info
              className="group-hover:scale-110 transition-transform"
              style={{ width: '14px', height: '14px', color: 'rgba(218,165,32,0.5)' }}
            />
          </button>
        </div>

        {/* Emblem Overlay — full hourglass with floating unboxed tooltips */}
        {showEmblem && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
            style={{ background: '#000' }}
            onClick={() => { setShowEmblem(false); setActiveHotspot(null); }}
            data-testid="emblem-overlay"
          >
            {/* Always-visible close button — pinned to viewport top-right, not to content */}
            <button
              onClick={(e) => { e.stopPropagation(); setShowEmblem(false); setActiveHotspot(null); }}
              className="fixed top-4 right-4 z-[10000] w-11 h-11 rounded-full flex items-center justify-center bg-amber-500/20 hover:bg-amber-500/40 border border-amber-400/50 text-amber-100 transition-all active:scale-95 shadow-lg"
              aria-label="Close hourglass story"
              title="Close (Esc)"
              data-testid="emblem-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <div
              className="relative flex flex-col items-center gap-2 sm:gap-3"
              onClick={(e) => { e.stopPropagation(); setActiveHotspot(null); }}
            >

              {/* Title */}
              <div className="text-center pointer-events-none select-none">
                <p
                  className="text-base sm:text-lg font-bold"
                  style={{
                    background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {activeHotspot ? (HOTSPOT_DATA[activeHotspot]?.title || 'Explore') : 'Why an Hourglass AND Stethoscope?'}
                </p>
                {!activeHotspot && (
                  <p className="text-[10px] text-amber-100/30 mt-1">Tap a marker to explore</p>
                )}
              </div>

              {/* Hourglass image with hotspot dots */}
              <div className="relative w-screen max-w-none -mx-4 sm:-mx-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_b19c70ec-4b92-4db4-be49-1b5c516f9472/artifacts/14rjo2s4_20260328_092048_0000.png"
                  alt="The Hourglass & Stethoscope"
                  className="block w-full h-auto select-none"
                  style={{ 
                    opacity: activeHotspot === 'convergence' ? 0.18 : (activeHotspot ? 0.8 : 0.92), 
                    transition: 'opacity 0.9s ease, filter 0.9s ease',
                    filter: activeHotspot === 'convergence' ? 'blur(8px) saturate(1.3)' : 'none',
                  }}
                  draggable={false}
                />

                {/* CONVERGENCE CINEMATIC OVERLAY */}
                {activeHotspot === 'convergence' && (
                  <div 
                    className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none"
                    style={{ animation: 'convergenceReveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
                  >
                    {/* Pulsing gold halo behind the text — amplified */}
                    <div 
                      className="absolute rounded-full"
                      style={{
                        top: '50%', left: '50%',
                        width: '360px', height: '360px',
                        transform: 'translate(-50%, -50%)',
                        background: 'radial-gradient(circle, rgba(255,215,0,0.35) 0%, rgba(218,165,32,0.2) 30%, transparent 65%)',
                        animation: 'convergenceGlow 2.4s ease-out infinite alternate',
                        filter: 'blur(30px)',
                      }}
                    />
                    {/* Secondary tighter halo */}
                    <div 
                      className="absolute rounded-full"
                      style={{
                        top: '50%', left: '50%',
                        width: '180px', height: '180px',
                        transform: 'translate(-50%, -50%)',
                        background: 'radial-gradient(circle, rgba(255,215,0,0.25) 0%, transparent 70%)',
                        animation: 'convergenceGlow 1.6s ease-out infinite alternate',
                        filter: 'blur(15px)',
                      }}
                    />
                    <div style={{
                      height: '1px', width: '0px',
                      background: 'linear-gradient(90deg, transparent, #ffd700, transparent)',
                      animation: 'convergenceLineReveal 0.8s 0.3s ease-out both',
                      marginBottom: '12px',
                      boxShadow: '0 0 12px rgba(255,215,0,0.6)',
                    }} />
                    <p
                      className="text-3xl sm:text-5xl font-black tracking-wide text-center px-4 relative"
                      style={{
                        background: 'linear-gradient(110deg, #fff 0%, #ffd700 30%, #ffef9c 45%, #fff 55%, #ffd700 70%, #daa520 100%)',
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        lineHeight: 1.15,
                        animation: 'convergenceShimmer 3s ease-in-out infinite',
                        filter: 'drop-shadow(0 0 14px rgba(255,215,0,0.5)) drop-shadow(0 2px 6px rgba(0,0,0,0.9))',
                      }}
                    >
                      Both can be done.
                    </p>
                    <p
                      className="text-sm sm:text-base text-amber-100 text-center mt-4 px-4 leading-relaxed relative"
                      style={{
                        textShadow: '0 2px 12px rgba(0,0,0,0.95), 0 0 20px rgba(255,215,0,0.3)',
                        textWrap: 'balance',
                        letterSpacing: '0.02em',
                      }}
                    >
                      Quality <em>and</em> quantity.
                      <br />
                      <span
                        className="font-black text-base sm:text-lg"
                        style={{
                          background: 'linear-gradient(110deg, #ffd700 0%, #fff 50%, #ffd700 100%)',
                          backgroundSize: '200% 100%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'convergenceShimmer 2.5s 0.4s ease-in-out infinite',
                        }}
                      >
                        Simultaneously.
                      </span>
                    </p>
                    <div style={{
                      height: '1px', width: '0px',
                      background: 'linear-gradient(90deg, transparent, #ffd700, transparent)',
                      animation: 'convergenceLineReveal 0.8s 0.5s ease-out both',
                      marginTop: '14px',
                      boxShadow: '0 0 12px rgba(255,215,0,0.6)',
                    }} />
                  </div>
                )}

                {/* Hotspot dots */}
                {Object.entries(HOTSPOT_DATA).map(([key, data]) => {
                  const pos = HOTSPOT_POS[key];
                  const isActive = activeHotspot === key;

                  {/* Hide convergence dot when convergence is active (cinematic takes over) */}
                  if (activeHotspot === 'convergence' && key === 'convergence') return null;

                  return (
                    <div
                      key={key}
                      className="absolute z-10 cursor-pointer"
                      style={{
                        top: pos.top,
                        left: pos.left,
                        transform: 'translate(-50%, -50%)',
                        opacity: activeHotspot === 'convergence' ? 0.3 : 1,
                        transition: 'opacity 0.5s ease',
                      }}
                      onClick={(e) => { e.stopPropagation(); setActiveHotspot(isActive ? null : key); }}
                      data-testid={`hotspot-${key}`}
                    >
                      {/* Dot */}
                      <div
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: isActive ? (data.brand ? '18px' : '14px') : (data.brand ? '12px' : '8px'),
                          height: isActive ? (data.brand ? '18px' : '14px') : (data.brand ? '12px' : '8px'),
                          background: isActive
                            ? (data.brand
                              ? 'radial-gradient(circle, #fff 0%, #ffd700 40%, #daa520 100%)'
                              : 'radial-gradient(circle, #ffd700 30%, #daa520 100%)')
                            : (data.brand
                              ? 'radial-gradient(circle, #ffd700 20%, #daa520 100%)'
                              : 'radial-gradient(circle, rgba(218,165,32,0.7) 30%, rgba(139,69,19,0.5) 100%)'),
                          border: isActive ? '2px solid #ffd700' : '1.5px solid rgba(218,165,32,0.5)',
                          boxShadow: isActive
                            ? '0 0 20px rgba(255,215,0,0.6)' : '0 0 8px rgba(218,165,32,0.3)',
                          animation: isActive ? 'none' : (data.brand ? 'convergenceDotPulse 2s ease-in-out infinite' : 'pulse 2.5s ease-in-out infinite'),
                        }}
                      />

                      {/* Desktop floating tooltip — zooms out from dot position */}
                      {isActive && (
                        <div
                          className="hidden sm:block absolute z-20 pointer-events-none"
                          style={{
                            ...(key === 'convergence' 
                              ? { 
                                  position: 'fixed',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '320px',
                                  textAlign: 'center',
                                }
                              : {
                                  ...(TOOLTIP_SIDE_STYLES[pos.side] || TOOLTIP_SIDE_STYLES.right),
                                  width: '180px',
                                }),
                            animation: key === 'convergence' 
                              ? 'zoomFromDotCenter 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                              : 'zoomFromDot 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                            transformOrigin: key === 'convergence' ? 'center center' : 'center center',
                          }}
                        >
                          <p
                            className={`${data.brand ? 'text-base font-black tracking-wide' : 'text-xs font-bold'} mb-1`}
                            style={{
                              background: data.brand
                                ? 'linear-gradient(135deg, #fff 0%, #ffd700 50%, #daa520 100%)'
                                : 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                              textWrap: 'balance',
                            }}
                          >{data.title}</p>
                          <p
                            className={`${data.brand ? 'text-sm' : 'text-[10px]'} leading-snug text-amber-100/80`}
                            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)', textWrap: 'balance' }}
                          >
                            {data.brand
                              ? data.desc.split('WriteRight PCM+').map((part, i, arr) =>
                                  i < arr.length - 1 ? (
                                    <span key={i}>{part}<span className="font-bold text-amber-300">WriteRight PCM+</span></span>
                                  ) : part
                                )
                              : data.desc
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile tooltip — below the image, zooms from dot */}
              {activeHotspot && HOTSPOT_DATA[activeHotspot] && (
                <div 
                  className="block sm:hidden px-2 max-w-[280px] text-center"
                  style={{ animation: 'zoomFromDot 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
                >
                  <p
                    className={`${HOTSPOT_DATA[activeHotspot].brand ? 'text-sm font-black tracking-wide' : 'text-sm font-bold'} mb-1`}
                    style={{
                      background: HOTSPOT_DATA[activeHotspot].brand
                        ? 'linear-gradient(135deg, #fff 0%, #ffd700 50%, #daa520 100%)'
                        : 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >{HOTSPOT_DATA[activeHotspot].title}</p>
                  <p className="text-xs leading-relaxed text-amber-100/80" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)', textWrap: 'balance' }}>
                    {HOTSPOT_DATA[activeHotspot].brand
                      ? HOTSPOT_DATA[activeHotspot].desc.split('WriteRight PCM+').map((part, i, arr) =>
                          i < arr.length - 1 ? (
                            <span key={i}>{part}<span className="font-bold text-amber-300">WriteRight PCM+</span></span>
                          ) : part
                        )
                      : HOTSPOT_DATA[activeHotspot].desc
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Spacer for fixed footer */}
        <div className="h-20 sm:h-16" aria-hidden="true" />
      </div>
      
      {/* Momentum Tracker - Fixed Footer Bar */}
      {momentumStats && !isLoadingMomentum && !showEmblem && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-40 px-3 sm:px-4 pt-1.5 sm:pt-2 pb-3 sm:pb-4"
          style={{
            background: 'linear-gradient(180deg, rgba(26, 10, 0, 0.97) 0%, rgba(13, 5, 0, 0.99) 100%)',
            borderTop: '1px solid rgba(218, 165, 32, 0.4)',
            boxShadow: '0 -2px 12px rgba(0,0,0,0.4)',
          }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-1.5 mb-0.5 sm:mb-1">
              <Activity className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-400 animate-pulse" />
              <span 
                className="text-[8px] sm:text-[10px] font-bold uppercase tracking-wider"
                style={{ 
                  background: 'linear-gradient(180deg, #4ade80 0%, #22c55e 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >Platform Stats</span>
            </div>
            
            <div className="flex justify-between w-full max-w-3xl mx-auto px-4 sm:px-8 md:px-12">
              {/* Total Documents */}
              <div className="text-center group cursor-pointer transition-transform hover:scale-110 active:scale-95" data-testid="stat-docs">
                <div 
                  className="text-lg sm:text-xl md:text-3xl font-bold"
                  style={{ 
                    background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {(momentumStats.total_documents ?? 0).toLocaleString()}
                </div>
                <div className="text-[9px] sm:text-[10px] text-amber-100/60">Docs</div>
              </div>
              
              {/* Time Saved */}
              <div className="text-center group cursor-pointer transition-transform hover:scale-110 active:scale-95" data-testid="stat-time">
                <div 
                  className="text-lg sm:text-xl md:text-3xl font-bold"
                  style={{ 
                    background: 'linear-gradient(180deg, #4ade80 0%, #22c55e 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {momentumStats.total_time_saved_hours ?? 0}h
                </div>
                <div className="text-[9px] sm:text-[10px] text-emerald-100/60">Saved</div>
              </div>
              
              {/* Active Users */}
              <div className="text-center group cursor-pointer transition-transform hover:scale-110 active:scale-95" data-testid="stat-users">
                <div 
                  className="text-lg sm:text-xl md:text-3xl font-bold"
                  style={{ 
                    background: 'linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {momentumStats.active_providers ?? 0}
                </div>
                <div className="text-[9px] sm:text-[10px] text-blue-100/60">Users</div>
              </div>
            </div>
          </div>
        </div>
      )}
      

      {/* Tutorial Slides Modal */}
      {showTutorial && (
        <TutorialSlides 
          onClose={() => setShowTutorial(false)} 
          onComplete={() => setShowTutorial(false)}
        />
      )}
      
      {/* Interactive Tutorial Modal with Screenshots */}
      {showInteractiveTutorial && (
        <TutorialSlideshow 
          onClose={() => setShowInteractiveTutorial(null)}
          initialTab={showInteractiveTutorial}
        />
      )}
      
      {/* What's New Modal — PREMIUM BRONZE/GOLD PLAQUE STYLE */}
      {showWhatsNew && (
        <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 pb-16" onClick={closeWhatsNew}>
          <div 
            className="w-full max-w-xl sm:max-w-2xl max-h-[85vh] overflow-hidden mb-12"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
              border: '2px solid #8b4513',
              borderRadius: '4px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 60px rgba(139, 69, 19, 0.15)',
              animation: 'slideUp 0.3s ease-out',
            }}
          >
            {/* Header — bronze ribbon */}
            <div 
              className="relative p-5 pb-4 overflow-hidden"
              style={{ borderBottom: '2px solid rgba(139, 69, 19, 0.6)' }}
            >
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(218,165,32,0.15) 0%, rgba(139,69,19,0.1) 100%)',
                      border: '1px solid rgba(218,165,32,0.3)',
                      borderRadius: '3px',
                    }}
                  >
                    <Sparkles className="w-5 h-5" style={{ color: '#daa520' }} />
                  </div>
                  <div>
                    <h2 
                      className="text-lg font-black tracking-tight"
                      style={{
                        background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >What's New</h2>
                    <p className="text-[11px] font-semibold" style={{ color: '#8b7355' }}>v{APP_VERSION} Release</p>
                  </div>
                </div>
                <button 
                  onClick={closeWhatsNew} 
                  className="p-1.5 transition-colors"
                  style={{ color: 'rgba(218,165,32,0.5)', borderRadius: '3px' }}
                  onMouseEnter={(e) => e.target.style.color = '#daa520'}
                  onMouseLeave={(e) => e.target.style.color = 'rgba(218,165,32,0.5)'}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Content — METALLIC CAROUSEL, tappable cards */}
            {(() => {
              // Draft items (admin-composed via "Ship Version") appear FIRST,
              // then the canonical current release items from /data/releaseNotes.js.
              const items = [...getDraftItems(), ...getCurrentReleaseItems()];
              const current = items[whatsNewSlide];
              const next = items[(whatsNewSlide + 1) % items.length];
              return (
                <div className="px-5 sm:px-6 pb-4 pt-3 relative" style={{ minHeight: '260px' }}>
                  {/* Main visible card — METALLIC GOLD, tappable */}
                  <div 
                    className="relative p-6 sm:p-8 transition-all duration-200 cursor-pointer select-none"
                    onClick={() => setWhatsNewSlide((whatsNewSlide + 1) % items.length)}
                    style={{
                      background: 'linear-gradient(170deg, #e8c84a 0%, #c9a62c 8%, #f5e17a 16%, #d4af37 24%, #a07c1c 36%, #dbbe45 48%, #f0d860 56%, #c9a62c 68%, #a88520 78%, #dbbe45 88%, #c9a62c 100%)',
                      borderRadius: '6px',
                      boxShadow: '0 1px 0 0 rgba(255,240,180,0.5) inset, 0 -1px 0 0 rgba(100,70,10,0.6) inset, 0 8px 24px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3), 0 0 0 1px rgba(160,124,28,0.7)',
                      border: '1px solid rgba(212,175,55,0.5)',
                      animation: 'fadeIn 0.35s ease-out',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    key={whatsNewSlide}
                    data-testid="whats-new-card"
                  >
                    {/* Metallic shine sweep overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '40%',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.05) 60%, transparent 100%)',
                      pointerEvents: 'none',
                      borderRadius: '6px 6px 0 0',
                    }} />
                    {/* Brushed metal texture lines */}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 3px)',
                      pointerEvents: 'none',
                      borderRadius: '6px',
                    }} />
                    {/* Edge bevel highlight */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent 5%, rgba(255,240,180,0.6) 30%, rgba(255,255,220,0.8) 50%, rgba(255,240,180,0.6) 70%, transparent 95%)',
                      pointerEvents: 'none',
                    }} />
                    <div className="relative flex items-start gap-4">
                      <span 
                        className="shrink-0 mt-0.5 text-[10px] sm:text-xs font-black tracking-[0.15em] px-3 py-1"
                        style={{ 
                          color: '#1a0a00',
                          background: 'rgba(26,10,0,0.18)',
                          border: '1px solid rgba(26,10,0,0.15)',
                          borderRadius: '3px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
                        }}
                      >
                        {current.tag}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xl sm:text-2xl font-black leading-tight" style={{ color: '#2a1500', textShadow: '0 1px 0 rgba(255,255,255,0.15)' }}>
                          {current.title}
                        </h4>
                        <p className="text-sm sm:text-base leading-relaxed mt-2" style={{ color: '#3d2200' }}>
                          {current.desc}
                        </p>
                      </div>
                    </div>
                    {/* Tap hint */}
                    <div className="relative flex items-center justify-end mt-3 gap-1.5" style={{ color: 'rgba(80,50,0,0.5)' }}>
                      <span className="text-[10px] font-semibold tracking-wide">TAP FOR NEXT</span>
                      <ChevronLeft className="w-3 h-3 rotate-180" />
                    </div>
                  </div>

                  {/* Peek of next card behind — darker metallic */}
                  <div 
                    className="mt-[-8px] mx-4 p-4 pointer-events-none"
                    style={{
                      background: 'linear-gradient(170deg, #8b7328 0%, #6b5a1e 30%, #7a6922 60%, #6b5a1e 100%)',
                      borderRadius: '0 0 6px 6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                      border: '1px solid rgba(107,90,30,0.4)',
                      borderTop: 'none',
                      opacity: 0.5,
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[9px] font-black px-2 py-0.5" style={{ color: '#2a1500', background: 'rgba(26,10,0,0.1)', borderRadius: '2px' }}>
                        {next.tag}
                      </span>
                      <span className="text-sm font-bold" style={{ color: '#2a1500' }}>{next.title}</span>
                    </div>
                  </div>

                  {/* Navigation dots + arrows — metallic style */}
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <button 
                      onClick={() => setWhatsNewSlide((whatsNewSlide - 1 + items.length) % items.length)}
                      className="p-2 transition-all active:scale-90"
                      style={{ 
                        color: '#daa520', 
                        background: 'linear-gradient(180deg, rgba(218,165,32,0.15) 0%, rgba(139,69,19,0.1) 100%)',
                        borderRadius: '4px',
                        border: '1px solid rgba(218,165,32,0.2)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
                      }}
                      data-testid="whats-new-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1.5">
                      {items.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setWhatsNewSlide(i)}
                          className="transition-all"
                          style={{
                            width: i === whatsNewSlide ? '20px' : '7px',
                            height: '7px',
                            borderRadius: '3.5px',
                            background: i === whatsNewSlide 
                              ? 'linear-gradient(180deg, #f5e17a 0%, #daa520 50%, #a88520 100%)' 
                              : 'rgba(218,165,32,0.2)',
                            boxShadow: i === whatsNewSlide 
                              ? '0 0 8px rgba(218,165,32,0.5), inset 0 1px 0 rgba(255,255,255,0.3)' 
                              : 'inset 0 1px 2px rgba(0,0,0,0.3)',
                            border: i === whatsNewSlide ? '1px solid rgba(245,225,122,0.4)' : '1px solid rgba(218,165,32,0.1)',
                          }}
                          data-testid={`whats-new-dot-${i}`}
                        />
                      ))}
                    </div>
                    <button 
                      onClick={() => setWhatsNewSlide((whatsNewSlide + 1) % items.length)}
                      className="p-2 transition-all active:scale-90"
                      style={{ 
                        color: '#daa520', 
                        background: 'linear-gradient(180deg, rgba(218,165,32,0.15) 0%, rgba(139,69,19,0.1) 100%)',
                        borderRadius: '4px',
                        border: '1px solid rgba(218,165,32,0.2)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
                      }}
                      data-testid="whats-new-next"
                    >
                      <ChevronLeft className="w-4 h-4 rotate-180" />
                    </button>
                  </div>
                  <p className="text-center text-[10px] mt-1.5 font-medium tracking-wide" style={{ color: '#6b5a3e' }}>{whatsNewSlide + 1} of {items.length}</p>
                </div>
              );
            })()}
            
            {/* Previous Versions — collapsed */}
            <div className="px-5 sm:px-6 pb-2">
              <details className="pt-3 mt-1" style={{ borderTop: '1px solid rgba(139,69,19,0.2)' }}>
                <summary 
                  className="text-[11px] cursor-pointer transition-colors py-1.5 select-none font-semibold"
                  style={{ color: '#6b5a3e' }}
                >
                  Previous updates (v2.5 &ndash; 2.9)
                </summary>
                <ul className="space-y-1 mt-2 pl-3" style={{ color: '#5a4a32' }}>
                  <li className="text-[11px]"><span className="font-bold" style={{ color: '#8b7355' }}>v2.9:</span> Team Board summary cards, Speed Chart 4-patient grid, PE section overhaul, Batch counter & milestones, Quick Settings dictation style</li>
                  <li className="text-[11px]"><span className="font-bold" style={{ color: '#8b7355' }}>v2.8:</span> Auto-fill appointment time, AR 25-50 export compliance, Codebase refactored (4100 to 3050 lines)</li>
                  <li className="text-[11px]"><span className="font-bold" style={{ color: '#8b7355' }}>v2.7:</span> Simultaneous generation, Quick Note Templates, Keyboard Shortcuts, Session Continuity</li>
                  <li className="text-[11px]"><span className="font-bold" style={{ color: '#8b7355' }}>v2.6:</span> Smarter clinical layout, Copy buttons fixed, ICD-10 drag-to-reorder, Premium landing page</li>
                  <li className="text-[11px]"><span className="font-bold" style={{ color: '#8b7355' }}>v2.5:</span> Patient labeling, Audio transcription auto-retry (4 attempts)</li>
                </ul>
              </details>
            </div>
            {/* Footer — metallic gold CTA */}
            <div className="p-4" style={{ borderTop: '2px solid rgba(139,69,19,0.4)', background: 'rgba(26,10,0,0.5)' }}>
              <button
                onClick={closeWhatsNew}
                className="w-full py-2.5 font-bold text-sm transition-all active:scale-[0.97]"
                style={{
                  background: 'linear-gradient(180deg, #f0d860 0%, #d4af37 20%, #c9a62c 50%, #a88520 80%, #c9a62c 100%)',
                  color: '#1a0a00',
                  borderRadius: '4px',
                  boxShadow: '0 4px 16px rgba(218,165,32,0.25), inset 0 1px 0 rgba(255,240,180,0.4), inset 0 -1px 0 rgba(100,70,10,0.4), 0 1px 3px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(212,175,55,0.4)',
                  textShadow: '0 1px 0 rgba(255,255,255,0.15)',
                  position: 'relative',
                }}
                data-testid="whats-new-got-it"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Origin Story Modal - CINEMATIC, SCREEN-SHARE READY */}
      {showOriginStory && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center" onClick={() => setShowOriginStory(false)}>
          <div 
            className="w-full h-full max-w-4xl max-h-[100vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, #0d0500 0%, #1a0a00 30%, #0d0500 100%)',
              animation: 'fadeIn 0.6s ease-out',
            }}
          >
            {/* Close button - floating top-right */}
            <button 
              onClick={() => setShowOriginStory(false)} 
              className="fixed top-4 right-4 z-[10000] p-2 rounded-full bg-black/60 hover:bg-black/80 text-amber-100/50 hover:text-amber-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Content — cinematic scroll */}
            <div className="max-w-2xl mx-auto px-8 py-12 sm:py-16 space-y-10"
              style={{ animation: 'slideUp 0.8s ease-out 0.2s backwards' }}
            >
              
              {/* Central Emblem */}
              <div className="flex flex-col items-center gap-6"
                style={{ animation: 'fadeIn 1s ease-out 0.4s backwards' }}
              >
                <img 
                  src="/emblem_icon.png" 
                  alt="WriteRight PCM+" 
                  className="w-24 h-24 sm:w-32 sm:h-32 object-contain opacity-90"
                />
                <p 
                  className="text-lg sm:text-xl font-semibold italic text-center"
                  style={{ 
                    background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >Where altruism and practicality meet.</p>
                <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent" />
              </div>
              
              {/* Story - CINEMATIC CARD */}
              <div 
                className="relative"
                style={{
                  padding: '3px',
                  background: 'linear-gradient(135deg, #cd853f 0%, #8b4513 20%, #daa520 50%, #8b4513 80%, #cd853f 100%)',
                  borderRadius: '4px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.6), 0 4px 12px rgba(139, 69, 19, 0.4)',
                  animation: 'fadeIn 1s ease-out 0.6s backwards',
                }}
              >
                {/* Inner content area */}
                <div 
                  className="p-8 sm:p-10 space-y-6 relative"
                  style={{
                    background: 'linear-gradient(180deg, #1a0a00 0%, #0d0500 50%, #1a0a00 100%)',
                    borderRadius: '2px',
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8)',
                  }}
                >
                  {/* Corner accents */}
                  <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-amber-600/60" />
                  <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-amber-600/60" />
                  <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-amber-600/60" />
                  <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-amber-600/60" />
                  
                  <p className="text-center" style={{ textWrap: 'balance' }}>
                    <span 
                      className="font-bold text-xl sm:text-2xl block mb-3"
                      style={{ 
                        background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      Built by a Military Nurse Practitioner
                    </span>
                    <span className="text-amber-100/90 text-base sm:text-lg leading-relaxed">that struggled with the impossible choice—quality care that makes patients feel heard, or timely charting that wouldn't jeopardize her licensure and privileging due to delinquent&nbsp;charts.</span>
                  </p>
                  
                  <div className="w-16 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent mx-auto" />
                  
                  {/* Subheader */}
                  <p 
                    className="text-center font-semibold text-base sm:text-lg italic"
                    style={{ 
                      background: 'linear-gradient(180deg, #d4af37 0%, #b8860b 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    The Insurmountable...
                  </p>
                  
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    It was the supporting medical memos for reassignment, compassionate stabilizations, extensions for PAR submissions in IPPS‑A—documents that live forever in admin records...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Letters to commanders for exception‑to‑policy considerations...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Knowing the regs that advocated for soldiers...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Reading every update on disqualifying conditions to qualify someone for MEB...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Ensuring records had the <span className="text-amber-400 font-semibold">substance</span> of ICD‑10 diagnoses, not just symptoms...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Transcribing diagnoses from specialty visits—the ones no one had time to do, buried in scanned copies of their visits...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    It was word getting out about a "thorough" provider.
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    The performance punishment.
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Being told she "cares too much"—repeatedly—by support staff up to supervisors.
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    Pushing through brilliant diagnosing riddled with imposter syndrome and doubt.
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    There had to be <span className="text-amber-400 font-semibold">a way to do both</span>—
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    without watching the clock when a patient needs to be heard...
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-base sm:text-lg" style={{ textWrap: 'pretty' }}>
                    when a letter could save their career.
                  </p>
                  
                  <div className="w-16 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent mx-auto" />
                  
                  <p 
                    className="text-center font-semibold text-lg sm:text-xl"
                    style={{ 
                      background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Not stubborn—Just determined.
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-center text-base" style={{ textWrap: 'pretty' }}>
                    Because the system is bigger than any one team.
                  </p>
                  <p className="text-amber-100/80 leading-relaxed text-center text-base" style={{ textWrap: 'pretty' }}>
                    Because burnout without balance was inevitable.
                  </p>
                  <p className="text-amber-100/70 text-center text-sm" style={{ textWrap: 'balance' }}>
                    Because the insurmountable was unsustainable in modern military medicine.
                  </p>
                </div>
              </div>
              
              {/* Closing — 5 Lines */}
              <div 
                className="relative"
                style={{
                  padding: '3px',
                  background: 'linear-gradient(135deg, #cd853f 0%, #8b4513 20%, #daa520 50%, #8b4513 80%, #cd853f 100%)',
                  borderRadius: '4px',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.5), 0 4px 10px rgba(139, 69, 19, 0.3)',
                  animation: 'fadeIn 1s ease-out 0.8s backwards',
                }}
              >
                <div 
                  className="p-8 sm:p-10 relative text-center space-y-2"
                  style={{
                    background: 'linear-gradient(180deg, #1a0a00 0%, #0d0500 50%, #1a0a00 100%)',
                    borderRadius: '2px',
                    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.8)',
                  }}
                >
                  {/* Corner accents */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-amber-600/50" />
                  <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-amber-600/50" />
                  <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-amber-600/50" />
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-amber-600/50" />
                  
                  <p 
                    className="font-bold text-base sm:text-lg italic"
                    style={{ 
                      background: 'linear-gradient(180deg, #d4af37 0%, #b8860b 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Built from burnout — Built to last
                  </p>
                  <p 
                    className="font-black text-xl sm:text-2xl tracking-tight"
                    style={{ 
                      background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    WriteRight PCM+
                  </p>
                  <p 
                    className="font-semibold text-base sm:text-lg italic"
                    style={{ 
                      background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Built to help us all—
                  </p>
                  <p className="text-amber-100/80 text-sm sm:text-base italic">
                    on both sides of the stethoscope
                  </p>
                  <div className="pt-2">
                    <p 
                      className="font-bold text-sm sm:text-base tracking-widest uppercase"
                      style={{ color: '#daa520' }}
                    >
                      People First
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer - Floating at bottom */}
            <div 
              className="sticky bottom-0 p-4 flex gap-3"
              style={{
                borderTop: '2px solid rgba(139, 69, 19, 0.5)',
                background: 'linear-gradient(180deg, #0d0500 0%, #1a0a00 100%)',
              }}
            >
              <button
                onClick={handleShare}
                className="flex-1 py-2.5 font-medium transition-all"
                style={{
                  background: copiedShare 
                    ? 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)'
                    : 'linear-gradient(180deg, #1a0a00 0%, #0d0500 100%)',
                  border: copiedShare ? '1px solid #22c55e' : '1px solid #8b4513',
                  color: copiedShare ? '#fff' : '#ffd700',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {copiedShare ? <CheckCircle2 className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                <span>{copiedShare ? 'Copied!' : 'Share'}</span>
              </button>
              <button
                onClick={() => setShowOriginStory(false)}
                className="flex-1 py-2.5 font-bold transition-all hover:scale-[1.02] text-center"
                style={{
                  background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                  color: '#1a0a00',
                  borderRadius: '4px',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Provider Stats Modal */}
      {showProviderStats && user && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 pb-16" onClick={() => setShowProviderStats(false)}>
          <div className="w-full max-w-4xl bg-card border border-border rounded-xl shadow-2xl max-h-[85vh] overflow-y-auto mb-12" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                My Productivity Stats
              </h2>
              <button onClick={() => setShowProviderStats(false)} className="p-1 hover:bg-secondary rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <ProviderDashboard user={user} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Patient Type Selector - Active Duty / Dependent / Civilian
 */
// ── Material Tile styling (Feb 23, 2026 v3) ────────────────────────────────
// Per user mockup: solid bold navy background, square tiles raised off the
// surface with distinct material treatments (stone, marble, metal, gem).
// Each tile gets its own saturated color with darker text where the material
// is light enough to support it. No starfield, no octagonal clip, no glass —
// just clean material cards floating above a single bold color backdrop.
// Feb 28, 2026 (v6.8.42) — exported so PatientWizard Step 1 can reuse the
// same gem/material treatment for its QUICK_LETTERS tiles instead of the
// dull outlined-card look. Keeping the source of truth here means one
// material palette across role-selector + wizard.
export const MATERIAL_STYLES = {
  // [bg gradient] | [text] | [extra-inner-detail]
  navy_gem:        'bg-gradient-to-br from-blue-500 via-blue-700 to-blue-900 text-white border-blue-300/40',
  slate_stone:    'bg-gradient-to-br from-slate-500 via-slate-700 to-slate-900 text-white border-slate-300/40',
  orange_agate:    'bg-gradient-to-br from-orange-300 via-orange-500 to-orange-700 text-orange-950 border-orange-200/40',
  emerald_gem:     'bg-gradient-to-br from-emerald-600 via-emerald-800 to-emerald-950 text-white border-emerald-300/40',
  burgundy_leather:'bg-gradient-to-br from-red-700 via-red-900 to-red-950 text-red-50 border-red-300/30',
  deep_purple:     'bg-gradient-to-br from-indigo-500 via-indigo-700 to-indigo-900 text-indigo-50 border-indigo-300/40',
  teal_slate:      'bg-gradient-to-br from-teal-700 via-teal-800 to-teal-950 text-white border-teal-300/40',
  brushed_gold:    'bg-gradient-to-br from-amber-500 via-amber-700 to-amber-900 text-amber-50 border-amber-300/50',
  dark_slate:      'bg-gradient-to-br from-slate-600 via-slate-700 to-slate-900 text-slate-50 border-slate-400/30',
  copper:          'bg-gradient-to-br from-orange-400 via-orange-600 to-amber-800 text-orange-50 border-orange-200/40',
  bronze:          'bg-gradient-to-br from-amber-600 via-amber-700 to-amber-900 text-amber-50 border-amber-300/40',
  obsidian:        'bg-gradient-to-br from-zinc-700 via-zinc-900 to-black text-zinc-50 border-zinc-400/30',
  rose_marble:     'bg-gradient-to-br from-rose-500 via-rose-700 to-rose-900 text-white border-rose-300/40',
  white_marble:    'bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 text-slate-900 border-slate-300/40',
  steel_blue:      'bg-gradient-to-br from-sky-500 via-sky-700 to-slate-900 text-sky-50 border-sky-300/40',
};

const MATERIAL_STYLES_LOCAL = MATERIAL_STYLES;

export const PatientTypeSelector = ({ onSelectType, onBack, onQuickLetter }) => {
  // Layout density toggle — Feb 23, 2026. Per Lori's note, on desktop the
  // role-selector wastes huge L/R padding. The toggle lets her constrain the
  // layout to a mobile-width column even on desktop (or vice versa), saved
  // in localStorage so it persists across sessions.
  const [layoutMode, setLayoutMode] = React.useState(() => {
    const stored = typeof window !== 'undefined' && localStorage.getItem('writeright_layout_mode');
    return stored || (window.innerWidth < 768 ? 'mobile' : 'desktop');
  });
  React.useEffect(() => {
    try { localStorage.setItem('writeright_layout_mode', layoutMode); } catch { /* quota */ }
  }, [layoutMode]);
  const containerMaxWidth = layoutMode === 'mobile' ? 'max-w-md' : 'max-w-3xl';

  const quickLetterSections = [
    {
      title: 'Before your appointment',
      items: [
        { id: 'visit_prep', label: 'Visit Prep Script', desc: 'Talking points for visit', icon: Sparkles, material: 'navy_gem', patientType: 'active_duty', documentMode: 'script', letterType: 'visit_prep_script' },
      ],
    },
    {
      title: 'Messages to your provider',
      items: [
        { id: 'portal_duty', label: 'Portal Msg: Duty', desc: 'Request profile/restrictions', icon: Shield, material: 'orange_agate', patientType: 'active_duty', documentMode: 'message', letterType: '', messageScenario: 'duty_limitation_letter' },
        { id: 'portal_meds', label: 'Portal Msg: Meds', desc: 'Refill or question', icon: Pill, material: 'emerald_gem', patientType: 'active_duty', documentMode: 'message', letterType: '', messageScenario: 'refill_same_pharmacy' },
        { id: 'portal_symptom', label: 'Portal Msg: Symptom', desc: 'New or worsening', icon: Thermometer, material: 'burgundy_leather', patientType: 'active_duty', documentMode: 'message', letterType: '', messageScenario: 'urgent_concern' },
        { id: 'portal_general', label: 'Portal Msg: General', desc: 'Any message request', icon: Inbox, material: 'deep_purple', patientType: 'active_duty', documentMode: 'message', letterType: '' },
      ],
    },
    {
      title: 'Letters from your provider',
      items: [
        { id: 'referral', label: 'Referral', desc: 'Request or issue', icon: Send, material: 'teal_slate', patientType: 'civilian', documentMode: 'letter', letterType: 'referral_request' },
        { id: 'fmla', label: 'FMLA', desc: 'Medical Leave', icon: CalendarDays, material: 'brushed_gold', patientType: 'civilian', documentMode: 'letter', letterType: 'fmla_intermittent' },
        { id: 'ada', label: 'ADA', desc: 'Workplace', icon: Accessibility, material: 'dark_slate', patientType: 'civilian', documentMode: 'letter', letterType: 'ada_accommodations' },
        { id: 'rtw', label: 'RTW', desc: 'Return to Work', icon: ArrowRightCircle, material: 'copper', patientType: 'civilian', documentMode: 'letter', letterType: 'return_to_work' },
        { id: 'command', label: 'Command', desc: 'Commander/1SG', icon: Medal, material: 'bronze', patientType: 'active_duty', documentMode: 'letter', letterType: 'letter_to_command' },
        { id: 'profile', label: 'Duty Limits', desc: 'Restrictions', icon: ListChecks, material: 'steel_blue', patientType: 'active_duty', documentMode: 'letter', letterType: 'custom', isLimitations: true },
      ],
    },
    {
      title: 'Appeals & advocacy',
      items: [
        { id: 'ides_appeal', label: 'IDES Appeal', desc: '1st person rebuttal', icon: Gavel, material: 'obsidian', patientType: 'active_duty', documentMode: 'letter', letterType: 'ides_appeal_soldier' },
        { id: 'buddy_statement', label: 'Buddy Statement', desc: 'Character witness for IDES', icon: UserCheck, material: 'rose_marble', patientType: 'active_duty', documentMode: 'letter', letterType: 'buddy_statement' },
        // Feb 28, 2026 (v6.8.39) — SSA & LTD long-term disability helpers
        // available to ALL patient types (not just veterans). These
        // generate 1st-person functional/ADL-limit statements aligned
        // with SSA Bluebook adult listings (14 body-system categories)
        // and private LTD insurer requirements (Lincoln Financial, Cigna,
        // Unum). Civilians/dependents/veterans pursuing SSDI in addition
        // to or instead of VA disability all have access.
        { id: 'ssa_disability', label: 'SSA Disability', desc: 'Bluebook functional statement (1st person)', icon: Accessibility, material: 'navy_gem', patientType: 'civilian', documentMode: 'letter', letterType: 'ssa_disability_statement' },
        { id: 'ltd_application', label: 'LTD Application', desc: 'Long-Term Disability statement (1st person)', icon: LifeBuoy, material: 'brushed_gold', patientType: 'civilian', documentMode: 'letter', letterType: 'ltd_application_statement' },
      ],
    },
    {
      title: 'After your appointment',
      items: [
        { id: 'joes_survey', label: 'JOES Survey', desc: 'Post-visit feedback (DHA)', icon: Star, material: 'brushed_gold', patientType: 'active_duty', documentMode: 'letter', letterType: 'joes_survey_response' },
        { id: 'other', label: 'Other', desc: 'Custom request', icon: MoreHorizontal, material: 'slate_stone', patientType: 'civilian', documentMode: 'letter', letterType: 'other' },
      ],
    },
  ];
  // Flat list retained for any legacy consumers (e.g., search) in case needed.
  const quickLetters = quickLetterSections.flatMap(s => s.items);

  const roleCards = [
    { type: 'active_duty', label: 'Active Duty / Guard / Reserve', desc: 'Service Member (AD, ARNG, USAR)', icon: Shield, material: 'bronze' },
    { type: 'dependent',   label: 'Dependent / Beneficiary',      desc: 'Family member, Retiree, Veteran with benefits', icon: Heart, material: 'navy_gem' },
    { type: 'civilian',    label: 'Civilian / Contractor',        desc: 'DoD civilian, Contractor, Other', icon: Building2, material: 'dark_slate' },
    // va_claims moved out of persona cards into the dedicated
    // "Need Help With Disability Claims?" section below (v6.8.49).
    // The question on this page is "what best describes you?" — disability
    // claim help is a TASK not a persona, so it gets its own surface.
  ];

  // Feb 28, 2026 (v6.8.49) — Disability-claim shortcut tiles.
  // Per user feedback the prior "VA Claims & Benefits" persona card didn't
  // belong as a "what best describes you?" answer. It's now a separate
  // task-oriented sub-section with three direct-shortcut buttons that
  // jump straight to the SSA / LTD / IDES letter type in the wizard.
  const disabilityShortcuts = [
    { id: 'ssa_disability', label: 'SSA Disability', desc: 'Social Security Bluebook functional statement', icon: Accessibility, material: 'navy_gem', letterType: 'ssa_disability_statement', patientType: 'civilian' },
    { id: 'ltd_application', label: 'LTD Application', desc: 'Long-Term Disability through your employer / insurer', icon: LifeBuoy, material: 'brushed_gold', letterType: 'ltd_application_statement', patientType: 'civilian' },
    { id: 'ides_appeal', label: 'IDES Appeal', desc: 'MEB / PEB rebuttal — active duty appeals', icon: Gavel, material: 'obsidian', letterType: 'ides_appeal_soldier', patientType: 'active_duty' },
  ];

  return (
    <div
      className="min-h-screen flex items-start sm:items-center justify-center p-4 relative"
      data-testid="role-selector-container"
      style={{
        // Match the landing page exactly — deep navy-black so this view feels
        // like the same app, not a different copper detour. Same HSL as the
        // RoleSelector backdrop at the top of this file.
        backgroundColor: 'hsl(220, 45%, 8%)',
        color: 'hsl(215, 25%, 88%)',
      }}
    >
      {/* Layout-density toggle — floats top-right. Lets Lori cycle between
          mobile-narrow (better on phone or for less padding on desktop) and
          desktop-wide layout without resizing the window. */}
      <button
        type="button"
        onClick={() => setLayoutMode(m => m === 'mobile' ? 'desktop' : 'mobile')}
        className="fixed top-3 right-3 z-50 flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-amber-950/70 hover:bg-amber-900 backdrop-blur-md border border-amber-300/40 text-amber-100 text-[10px] font-bold uppercase tracking-wider shadow-lg transition-all hover:-translate-y-0.5"
        title={`Switch to ${layoutMode === 'mobile' ? 'desktop-wide' : 'mobile-narrow'} layout`}
        data-testid="layout-mode-toggle"
      >
        {layoutMode === 'mobile' ? <Smartphone className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{layoutMode === 'mobile' ? 'Mobile' : 'Desktop'}</span>
      </button>

      <div className={`${containerMaxWidth} w-full space-y-6 py-6 transition-[max-width] duration-300`}>
        <button onClick={onBack} className="text-amber-100/80 hover:text-white flex items-center gap-2">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        <div className="text-center space-y-2">
          <h2
            className="text-2xl tracking-tight"
            style={{
              fontFamily: '"Cinzel", "Libre Baskerville", serif',
              fontWeight: 700,
              letterSpacing: '0.06em',
              background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >What best describes you?</h2>
          <p className="text-amber-100/60 text-sm italic" style={{ fontFamily: '"Libre Baskerville", serif' }}>
            This helps us use the right language and templates
          </p>
        </div>

        {/* UNIFIED DOSSIER FRAME — bronze-trimmed plaque that contains role
            cards + Quick Start so the whole patient-side flow reads as ONE
            coherent surface, matching the landing-page luxe aesthetic. */}
        <div
          className="copper-shine-frame"
          style={{
            padding: '3px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 60px rgba(139, 69, 19, 0.18)',
          }}
        >
          <div
            className="p-5 sm:p-7 space-y-6"
            style={{
              background: 'linear-gradient(180deg, hsl(220, 45%, 10%) 0%, hsl(220, 45%, 6%) 100%)',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(218,165,32,0.12)',
            }}
          >
            <div className="flex flex-col items-center" style={{ gap: '20px' }}>
              {roleCards.map(rc => {
            const Icon = rc.icon;
            const materialClasses = MATERIAL_STYLES[rc.material] || MATERIAL_STYLES.slate_stone;
            return (
              <button
                key={rc.type}
                onClick={() => onSelectType(rc.type)}
                data-testid={`role-card-${rc.type}`}
                style={{
                  borderRadius: 0,
                  maxWidth: '520px',
                  minHeight: '78px',
                  boxShadow: [
                    'inset 0 4px 0 rgba(255,255,255,0.6)',
                    'inset 0 -5px 0 rgba(0,0,0,0.55)',
                    'inset 4px 0 0 rgba(255,255,255,0.18)',
                    'inset -4px 0 0 rgba(0,0,0,0.38)',
                    'inset 0 10px 18px -8px rgba(255,255,255,0.28)',
                    'inset 0 -14px 22px -10px rgba(0,0,0,0.5)',
                    '0 6px 14px -2px rgba(0,0,0,0.55)',
                    '0 2px 3px rgba(0,0,0,0.4)',
                  ].join(', '),
                }}
                className={`relative w-full pl-5 pr-6 py-4 border-2 transition-all flex items-center justify-center text-center gap-5 rounded-none ${materialClasses}
                            hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99]`}
              >
                <div
                  className="pointer-events-none absolute inset-[6px]"
                  style={{
                    border: '1px solid rgba(255, 215, 130, 0.28)',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.25)',
                  }}
                />
                <Icon
                  className="w-9 h-9 shrink-0 relative z-10"
                  strokeWidth={2}
                  style={{
                    color: 'rgba(255, 244, 214, 0.92)',
                    filter: [
                      'drop-shadow(0 -1px 0 rgba(0,0,0,0.85))',
                      'drop-shadow(0 1px 0 rgba(255,255,255,0.55))',
                      'drop-shadow(0 -2px 3px rgba(0,0,0,0.45))',
                      'drop-shadow(0 2px 1px rgba(255,235,180,0.25))',
                    ].join(' '),
                  }}
                />
                <div className="relative z-10 text-center">
                  <h3
                    className="leading-tight break-words"
                    style={{
                      fontFamily: '"Cinzel", "Libre Baskerville", serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      color: 'rgba(255, 248, 224, 0.96)',
                      textShadow: [
                        '0 -1px 0 rgba(0,0,0,0.55)',
                        '0 1px 0 rgba(255,255,255,0.18)',
                        '0 2px 4px rgba(0,0,0,0.4)',
                      ].join(', '),
                    }}
                  >
                    {rc.label}
                  </h3>
                  <p
                    className="text-[11px] mt-1 break-words"
                    style={{
                      fontFamily: '"Libre Baskerville", serif',
                      fontStyle: 'italic',
                      letterSpacing: '0.03em',
                      color: 'rgba(255, 248, 224, 0.7)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {rc.desc}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Feb 28, 2026 (v6.8.49) — "Need Help With Disability Claims?"
            sub-section. Used to be a persona card ("VA Claims & Benefits")
            but disability claim help is a TASK not a persona, so it's
            broken out into its own labeled surface with 3 direct shortcuts
            to the SSA / LTD / IDES letter types. Clicking a shortcut
            jumps straight into the wizard with the right letter type
            pre-loaded and the right patient_type pre-set (so persona
            filtering still works). */}
        {onQuickLetter && (
          <div className="pt-1" data-testid="disability-shortcuts-section">
            <div className="flex items-center justify-center gap-2 mb-3 mt-1">
              <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.4), transparent)' }} />
              <span
                className="flex items-center gap-2 text-[11px] px-3 py-1 tracking-widest font-bold uppercase"
                style={{
                  fontFamily: '"Cinzel", serif',
                  letterSpacing: '0.18em',
                  background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                <BadgeCheck className="w-3 h-3 text-amber-300" /> Need Help With Disability Claims?
              </span>
              <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.4), transparent)' }} />
            </div>
            <p className="text-center text-[11px] text-amber-100/65 italic mb-4 px-4" style={{ fontFamily: '"Libre Baskerville", serif' }}>
              Skip persona pick — jump straight to the disability letter you need.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[640px] mx-auto">
              {disabilityShortcuts.map(ds => {
                const Icon = ds.icon;
                const materialClasses = MATERIAL_STYLES[ds.material] || MATERIAL_STYLES.slate_stone;
                return (
                  <button
                    key={ds.id}
                    type="button"
                    onClick={() => onQuickLetter(ds.patientType, ds.letterType)}
                    data-testid={`disability-shortcut-${ds.id}`}
                    className={`group relative h-[100px] flex flex-col items-center justify-center gap-1 p-2 border
                                ${materialClasses}
                                hover:-translate-y-1 active:translate-y-0 active:scale-[0.97] transition-all duration-200`}
                    style={{
                      borderRadius: 0,
                      boxShadow: [
                        'inset 0 3px 0 rgba(255,255,255,0.55)',
                        'inset 0 -4px 0 rgba(0,0,0,0.5)',
                        'inset 3px 0 0 rgba(255,255,255,0.18)',
                        'inset -3px 0 0 rgba(0,0,0,0.35)',
                        '0 5px 10px -2px rgba(0,0,0,0.55)',
                      ].join(', '),
                    }}
                  >
                    <span
                      className="pointer-events-none absolute inset-[4px]"
                      style={{ border: '1px solid rgba(255, 215, 130, 0.22)' }}
                    />
                    <Icon className="w-5 h-5 mb-0.5 relative z-10" strokeWidth={2}
                      style={{
                        color: 'rgba(255, 244, 214, 0.92)',
                        filter: 'drop-shadow(0 -1px 0 rgba(0,0,0,0.85)) drop-shadow(0 1px 0 rgba(255,255,255,0.5))',
                      }}
                    />
                    <span className="text-[11px] leading-tight text-center px-1 relative z-10"
                      style={{
                        fontFamily: '"Cinzel", serif',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: 'rgba(255, 248, 224, 0.96)',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      }}
                    >{ds.label}</span>
                    <span className="text-[9px] leading-tight text-center px-1 relative z-10 italic"
                      style={{
                        fontFamily: '"Libre Baskerville", serif',
                        color: 'rgba(255, 248, 224, 0.65)',
                      }}
                    >{ds.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Start — INSIDE the dossier */}
        {onQuickLetter && (
          <div className="pt-2" data-testid="quick-letters-type-selector">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.4), transparent)' }} />
              <span
                className="text-xs flex items-center gap-1.5"
                style={{
                  fontFamily: '"Cinzel", serif',
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  background: 'linear-gradient(180deg, #ffd700 0%, #daa520 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                <Zap className="w-3 h-3 text-amber-300" /> Quick Start
              </span>
              <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.4), transparent)' }} />
            </div>
            <p
              className="text-center text-xs text-amber-100/55 mb-4 italic"
              style={{ fontFamily: '"Libre Baskerville", serif' }}
            >
              Know what you need? Tap to skip ahead.
            </p>
            {/* v6.8.49 — More breathing room between sections per user
                feedback that headers were "crammed". space-y-8 (was 6),
                padded section divider, larger uppercase header font. */}
            <div className="space-y-8 mt-2" data-testid="quick-letters-sections">
              {quickLetterSections.map((section) => (
                <div key={section.title} className="pt-2">
                  <h4
                    className="text-[12px] mb-4 text-center sm:text-left"
                    style={{
                      fontFamily: '"Cinzel", serif',
                      fontWeight: 700,
                      letterSpacing: '0.20em',
                      textTransform: 'uppercase',
                      color: 'rgba(218,165,32,0.92)',
                      paddingBottom: '8px',
                      borderBottom: '1px solid rgba(218,165,32,0.25)',
                    }}
                  >
                    {section.title}
                  </h4>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {section.items.map(ql => {
                      const Icon = ql.icon;
                      const materialClasses = MATERIAL_STYLES[ql.material] || MATERIAL_STYLES.slate_stone;
                      return (
                        <button
                          key={ql.id}
                          onClick={() => onQuickLetter(ql)}
                          data-testid={`quick-start-${ql.id}`}
                          style={{
                            borderRadius: 0,
                            boxShadow: [
                              'inset 0 3px 0 rgba(255,255,255,0.55)',
                              'inset 0 -4px 0 rgba(0,0,0,0.5)',
                              'inset 3px 0 0 rgba(255,255,255,0.18)',
                              'inset -3px 0 0 rgba(0,0,0,0.35)',
                              '0 5px 10px -2px rgba(0,0,0,0.55)',
                            ].join(', '),
                          }}
                          className={`group relative h-[100px] flex flex-col items-center justify-center gap-1 p-2 border rounded-none ${materialClasses}
                                      hover:-translate-y-1 active:translate-y-0 active:scale-[0.97] transition-all duration-200`}
                        >
                          <div
                            className="pointer-events-none absolute inset-[4px]"
                            style={{ border: '1px solid rgba(255, 215, 130, 0.22)' }}
                          />
                          <Icon
                            className="w-5 h-5 mb-0.5 relative z-10"
                            strokeWidth={2}
                            style={{
                              color: 'rgba(255, 244, 214, 0.92)',
                              filter: [
                                'drop-shadow(0 -1px 0 rgba(0,0,0,0.85))',
                                'drop-shadow(0 1px 0 rgba(255,255,255,0.5))',
                                'drop-shadow(0 -1px 2px rgba(0,0,0,0.4))',
                              ].join(' '),
                            }}
                          />
                          <span
                            className="text-[10px] leading-tight text-center px-1 relative z-10"
                            style={{
                              fontFamily: '"Cinzel", serif',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: 'rgba(255, 248, 224, 0.96)',
                              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            }}
                          >{ql.label}</span>
                          <span
                            className="text-[9px] leading-tight text-center px-1 relative z-10 italic"
                            style={{
                              fontFamily: '"Libre Baskerville", serif',
                              color: 'rgba(255, 248, 224, 0.65)',
                            }}
                          >{ql.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};
